import React, { createContext, useContext, useMemo, useState } from 'react';

/**
 * Holds the site header's actual rendered height (header chrome + any
 * sub-headers + the informational-message banner). Sticky content that parks
 * below the header — currently only the menu's category nav — reads this so it
 * offsets itself exactly, even when the banner wraps to multiple lines.
 *
 * Page content does NOT need this: the header lives in a sticky block that
 * occupies layout space, so normal-flow content already starts beneath it.
 *
 * `measuredHeight` is `undefined` until the header measures itself (first
 * render / SSR); consumers fall back to a computed estimate, see
 * `useHeaderHeight`.
 */
interface HeaderHeightContextValue {
    measuredHeight?: number;
    setMeasuredHeight: (height: number) => void;
}

const HeaderHeightContext = createContext<HeaderHeightContextValue | undefined>(undefined);

export const HeaderHeightProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [measuredHeight, setMeasuredHeight] = useState<number | undefined>(undefined);

    const value = useMemo(() => ({ measuredHeight, setMeasuredHeight }), [measuredHeight]);

    return <HeaderHeightContext.Provider value={value}>{children}</HeaderHeightContext.Provider>;
};

/**
 * Returns the measured-header-height context. Safe to call outside a provider
 * (e.g. in tests): returns an inert value so consumers fall back to their
 * computed estimate.
 */
export const useMeasuredHeaderHeight = (): HeaderHeightContextValue =>
    useContext(HeaderHeightContext) ?? { measuredHeight: undefined, setMeasuredHeight: () => {} };
